// Dynamic Diesels — Product page (PDP) behavior.
// - Variant switching (radio pills → find matching variant → update ID,
//   price, availability, image, URL history).
// - Quantity stepper.
// - Thumbnail click → swap main image.
// Data comes from a JSON script tag rendered by the section.

(function () {
  'use strict';

  function init() {
    document.querySelectorAll('[data-mp]').forEach(setup);
    setupTabs();
    setupSticky();
    setupVariantModal();
    window.addEventListener('dd:vehicle-storage-change', setupFitmentFromGarage);
    window.addEventListener('storage', (event) => {
      if (event.key === window.DdVehicleStorage?.STORAGE_KEY) {
        setupFitmentFromGarage();
      }
    });
    // Compare against the garage's active saved vehicle (DdVehicleStorage.selected).
    whenVehicleLibsReady(setupFitmentFromGarage);
    setupGallery();
    setupInstallVideo();
  }

  // Section scripts can run before theme vehicle libs finish registering.
  // Wait until DdVehicleStorage is present so we read the same active vehicle
  // the garage shows (localStorage → getSelectedVehicle).
  function whenVehicleLibsReady(callback) {
    if (window.DdVehicleStorage && window.DdYmeFitment) {
      callback();
      return;
    }

    let attempts = 0;
    const maxAttempts = 50;
    const timer = setInterval(() => {
      attempts += 1;
      if (window.DdVehicleStorage && window.DdYmeFitment) {
        clearInterval(timer);
        callback();
        return;
      }
      if (attempts >= maxAttempts) {
        clearInterval(timer);
        callback();
      }
    }, 20);
  }

  // Cap the vertical thumb strip to the main stage height and scroll it with
  // up/down arrows when a product has more media than fit. Desktop only —
  // on mobile the strip is a horizontal scroller.
  function setupGallery() {
    document.querySelectorAll('.dd-mp__gallery').forEach((gallery) => {
      const wrap = gallery.querySelector('.dd-mp__thumbs-wrap');
      const thumbs = gallery.querySelector('[data-mp-thumbs]');
      const stage = gallery.querySelector('[data-mp-main-stage]');
      const mainImg = gallery.querySelector('[data-mp-main-image]');
      if (!wrap || !thumbs || !stage) return;
      const navs = Array.prototype.slice.call(gallery.querySelectorAll('[data-mp-thumb-nav]'));

      function updateNavState() {
        const atTop = thumbs.scrollTop <= 1;
        const atBottom = thumbs.scrollTop + thumbs.clientHeight >= thumbs.scrollHeight - 1;
        navs.forEach((n) => {
          const dir = parseInt(n.getAttribute('data-mp-thumb-nav'), 10);
          n.classList.toggle('is-disabled', (dir < 0 && atTop) || (dir > 0 && atBottom));
        });
      }

      function sync() {
        const desktop = window.matchMedia('(min-width: 750px)').matches;
        if (!desktop) {
          wrap.style.height = '';
          navs.forEach((n) => { n.hidden = true; });
          return;
        }
        const h = stage.clientHeight;
        if (h) wrap.style.height = h + 'px';
        const overflowing = thumbs.scrollHeight > thumbs.clientHeight + 2;
        navs.forEach((n) => { n.hidden = !overflowing; });
        updateNavState();
      }

      navs.forEach((n) => {
        n.addEventListener('click', () => {
          const dir = parseInt(n.getAttribute('data-mp-thumb-nav'), 10);
          thumbs.scrollBy({ top: dir * thumbs.clientHeight * 0.8, behavior: 'smooth' });
        });
      });
      thumbs.addEventListener('scroll', updateNavState, { passive: true });

      sync();
      if (mainImg && !mainImg.complete) mainImg.addEventListener('load', sync);
      window.addEventListener('resize', sync);
    });
  }

  function clearVideoPlayer(videoEl) {
    if (!videoEl) return;
    const player = videoEl.querySelector('[data-mp-video-player]');
    const playBtn = videoEl.querySelector('[data-mp-video-play]');
    const poster = videoEl.querySelector('[data-mp-video-poster]');
    if (player) {
      player.innerHTML = '';
      player.hidden = true;
    }
    if (playBtn) playBtn.hidden = false;
    if (poster) poster.hidden = false;
  }

  function showMainImage(root, src) {
    const mainImg = root.querySelector('[data-mp-main-image]');
    const videoEl = root.querySelector('[data-mp-main-video]');
    clearVideoPlayer(videoEl);
    if (videoEl) videoEl.hidden = true;
    if (mainImg) {
      mainImg.hidden = false;
      if (src) mainImg.src = src;
    }
  }

  function showMainVideo(root, opts) {
    const mainImg = root.querySelector('[data-mp-main-image]');
    const videoEl = root.querySelector('[data-mp-main-video]');
    if (!videoEl) return;
    clearVideoPlayer(videoEl);
    if (mainImg) mainImg.hidden = true;
    videoEl.hidden = false;
    videoEl.setAttribute('data-mp-type', opts.type || 'external_video');
    if (opts.embed) {
      videoEl.setAttribute('data-mp-embed', opts.embed);
      videoEl.removeAttribute('data-mp-video-src');
    } else {
      videoEl.removeAttribute('data-mp-embed');
    }
    if (opts.videoSrc) {
      videoEl.setAttribute('data-mp-video-src', opts.videoSrc);
      videoEl.removeAttribute('data-mp-embed');
    } else if (!opts.embed) {
      videoEl.removeAttribute('data-mp-video-src');
    }
    const poster = videoEl.querySelector('[data-mp-video-poster]');
    if (poster && opts.poster) poster.src = opts.poster;
  }

  function playMainVideo(videoEl) {
    if (!videoEl) return;
    const player = videoEl.querySelector('[data-mp-video-player]');
    const playBtn = videoEl.querySelector('[data-mp-video-play]');
    const poster = videoEl.querySelector('[data-mp-video-poster]');
    if (!player) return;

    const embed = videoEl.getAttribute('data-mp-embed');
    const videoSrc = videoEl.getAttribute('data-mp-video-src');
    player.innerHTML = '';

    if (embed) {
      const iframe = document.createElement('iframe');
      iframe.src = embed;
      iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
      iframe.setAttribute('allowfullscreen', '');
      iframe.setAttribute('title', 'Product video');
      iframe.setAttribute('loading', 'lazy');
      player.appendChild(iframe);
    } else if (videoSrc) {
      const video = document.createElement('video');
      video.src = videoSrc;
      video.controls = true;
      video.autoplay = true;
      video.playsInline = true;
      player.appendChild(video);
    } else {
      return;
    }

    player.hidden = false;
    if (playBtn) playBtn.hidden = true;
    if (poster) poster.hidden = true;
  }

  function setupInstallVideo() {
    document.querySelectorAll('[data-mp-install-video]').forEach((wrap) => {
      if (wrap.dataset.mpInstallBound === '1') return;
      wrap.dataset.mpInstallBound = '1';
      wrap.addEventListener('click', () => playInstallVideo(wrap));
    });
  }

  function playInstallVideo(wrap) {
    if (!wrap || wrap.dataset.mpInstallPlaying === '1') return;
    const embed = wrap.getAttribute('data-mp-embed');
    const player = wrap.querySelector('[data-mp-install-player]');
    const playBtn = wrap.querySelector('[data-mp-install-play]');
    const poster = wrap.querySelector('[data-mp-install-poster]');
    if (!embed || !player) return;

    const iframe = document.createElement('iframe');
    iframe.src = embed;
    iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('title', 'Installation video');
    iframe.setAttribute('loading', 'lazy');
    player.innerHTML = '';
    player.appendChild(iframe);
    player.hidden = false;
    if (playBtn) playBtn.hidden = true;
    if (poster) poster.hidden = true;
    wrap.dataset.mpInstallPlaying = '1';
    wrap.style.cursor = 'default';
  }

  function setupSticky() {
    const sticky = document.querySelector('[data-mp-sticky]');
    const addBtn = document.querySelector('[data-mp-add]');
    if (!sticky || !addBtn) return;

    // Tuner Depot-style: show sticky once the in-page ATC has scrolled off,
    // while the user is scrolling down. Hide after 150px of upward scroll
    // (or when the in-page ATC returns into view). Overlays the mobile tab bar.
    const HIDE_AFTER_UP_PX = 150;
    let lastScrollY = window.scrollY;
    let upScrollDistance = 0;

    function setStickyVisible(isVisible) {
      sticky.classList.toggle('is-visible', isVisible);
      document.body.classList.toggle('dd-sticky-atc-visible', isVisible);
    }

    function updateStickyVisibility() {
      const currentScrollY = window.scrollY;
      const scrollDelta = currentScrollY - lastScrollY;
      if (addBtn.getBoundingClientRect().top < 0) {
        if (scrollDelta > 0) {
          upScrollDistance = 0;
          setStickyVisible(true);
        } else if (scrollDelta < 0) {
          upScrollDistance += -scrollDelta;
          if (upScrollDistance >= HIDE_AFTER_UP_PX) setStickyVisible(false);
        }
      } else {
        upScrollDistance = 0;
        setStickyVisible(false);
      }
      lastScrollY = currentScrollY;
    }

    window.addEventListener('scroll', updateStickyVisibility, { passive: true });
    setStickyVisible(addBtn.getBoundingClientRect().top < 0);

    // Sticky Add-to-Cart submits the main product form.
    const stickyAdd = sticky.querySelector('[data-mp-sticky-add]');
    if (stickyAdd) {
      stickyAdd.addEventListener('click', () => {
        const form = document.querySelector('.dd-mp__form');
        // requestSubmit fires the submit event so dd-cart.js can intercept (AJAX + drawer).
        if (form) form.requestSubmit();
      });
    }
  }

  function setupVariantModal() {
    const modal = document.querySelector('[data-mp-variant-modal]');
    const openBtn = document.querySelector('[data-mp-sticky-variant]');
    if (!modal) return;

    function openModal() {
      // Sync modal inputs with main form's current values before showing.
      modal.querySelectorAll('[data-mp-option-name]').forEach((modalGroup) => {
        const name = modalGroup.getAttribute('data-mp-option-name');
        const mainGroup = findOptionGroup(document.querySelector('.dd-mp__form'), name);
        if (mainGroup) setOptionValue(modalGroup, getOptionValue(mainGroup));
      });
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('dd-variant-open');
      document.body.style.overflow = 'hidden';
    }

    function closeModal() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('dd-variant-open');
      document.body.style.overflow = '';
    }

    if (openBtn) openBtn.addEventListener('click', openModal);
    modal.querySelectorAll('[data-mp-variant-close]').forEach((el) => el.addEventListener('click', closeModal));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) closeModal();
    });

    // Modal input changes mirror into the main form + fire its change handler.
    modal.querySelectorAll('[data-mp-modal-option-input]').forEach((modalInput) => {
      modalInput.addEventListener('change', () => {
        const name = modalInput.getAttribute('data-mp-modal-option-input');
        const mainGroup = findOptionGroup(document.querySelector('.dd-mp__form'), name);
        if (!mainGroup) return;
        const el = setOptionValue(mainGroup, modalInput.value);
        if (el) el.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    // Modal Add-to-Cart submits the main form.
    const modalAdd = modal.querySelector('[data-mp-modal-add]');
    if (modalAdd) {
      modalAdd.addEventListener('click', () => {
        const form = document.querySelector('.dd-mp__form');
        if (form) form.requestSubmit();
        closeModal();
      });
    }
  }

  function setupFitmentFromGarage() {
    const callout = document.querySelector('[data-dd-fitcallout]');
    const vehicle = window.DdVehicleStorage?.getSelectedVehicle();
    const hasVehicle = Boolean(vehicle?.make);
    const vehicleLabel = hasVehicle
      ? window.DdVehicleStorage.formatVehicleDisplay(vehicle)
      : '';

    const canCheck = Boolean(hasVehicle && callout && window.DdYmeFitment);
    const fits = canCheck
      ? window.DdYmeFitment.productMatchesVehicle(callout, vehicle)
      : false;

    if (callout) updateFitcallout(callout, { hasVehicle, canCheck, fits, vehicleLabel });
  }

  function updateFitcallout(callout, { hasVehicle, canCheck, fits, vehicleLabel }) {
    const icon = callout.querySelector('[data-dd-fitcallout-icon]');
    const heading = callout.querySelector('[data-dd-fitcallout-heading]');
    const subheading = callout.querySelector('[data-dd-fitcallout-subheading]');
    const vehicleEl = callout.querySelector('[data-dd-fitcallout-vehicle]');
    const action = callout.querySelector('[data-dd-fitcallout-action]');
    const defaultTitle = callout.getAttribute('data-fitcallout-title') || 'Select your Vehicle';
    const defaultBody =
      callout.getAttribute('data-fitcallout-body') ||
      'Choose your vehicle to confirm this product fits your truck.';

    callout.classList.remove(
      'dd-mp__fitcallout--unselected',
      'dd-mp__fitcallout--fits',
      'dd-mp__fitcallout--no-fit'
    );

    if (!hasVehicle) {
      callout.classList.add('dd-mp__fitcallout--unselected');
      if (icon) icon.setAttribute('icon', 'material-symbols:directions-car-outline');
      if (heading) heading.textContent = defaultTitle;
      if (subheading) {
        subheading.textContent = defaultBody;
        subheading.hidden = false;
      }
      if (vehicleEl) {
        vehicleEl.textContent = '';
        vehicleEl.hidden = true;
      }
      if (action) action.textContent = 'Add Vehicle';
      callout.setAttribute('aria-label', defaultTitle);
      return;
    }

    if (!canCheck) {
      callout.classList.add('dd-mp__fitcallout--unselected');
      if (icon) icon.setAttribute('icon', 'material-symbols:directions-car-outline');
      if (heading) heading.textContent = 'Your selected vehicle';
      if (subheading) subheading.hidden = true;
      if (vehicleEl) {
        vehicleEl.textContent = vehicleLabel;
        vehicleEl.hidden = false;
      }
      if (action) action.textContent = 'Change';
      callout.setAttribute('aria-label', 'Selected vehicle: ' + vehicleLabel + '. Change vehicle');
      return;
    }

    callout.classList.add(fits ? 'dd-mp__fitcallout--fits' : 'dd-mp__fitcallout--no-fit');
    if (icon) {
      icon.setAttribute(
        'icon',
        fits ? 'material-symbols:check-circle' : 'material-symbols:warning-outline'
      );
    }
    if (heading) {
      heading.textContent = fits
        ? 'This product fits your vehicle'
        : 'This product does not fit your vehicle';
    }
    if (subheading) subheading.hidden = true;
    if (vehicleEl) {
      vehicleEl.textContent = vehicleLabel;
      vehicleEl.hidden = false;
    }
    if (action) action.textContent = 'Change';
    callout.setAttribute(
      'aria-label',
      (fits ? 'Fits your ' : 'Does not fit your ') + vehicleLabel + '. Change vehicle'
    );
  }

  function setupTabs() {
    document.querySelectorAll('.dd-mp__tab').forEach((tab) => {
      tab.addEventListener('click', () => {
        const target = tab.getAttribute('data-mp-tab');
        const scope = tab.closest('.dd-mp__tabs') || document;

        scope.querySelectorAll('.dd-mp__tab').forEach((t) => {
          const active = t === tab;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        scope.querySelectorAll('.dd-mp__tab-pane').forEach((pane) => {
          pane.classList.toggle('is-active', pane.getAttribute('data-mp-pane') === target);
        });
      });
    });
  }

  function setup(root) {
    const dataEl = root.querySelector('[data-mp-json]');
    if (!dataEl) return;
    let data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
    const variants = data.variants || [];
    if (!variants.length) return;
    const addonVariants = data.addon_variants || [];

    const optionInputs = Array.from(root.querySelectorAll('[data-mp-option-input]'));
    const idInput = root.querySelector('[data-mp-variant-id]');
    const priceEl = root.querySelector('[data-mp-price]');
    const compareEl = root.querySelector('[data-mp-compare]');
    const saveEl = root.querySelector('[data-mp-save]');
    const addBtn = root.querySelector('[data-mp-add]');
    const addLabel = root.querySelector('[data-mp-add-label]');
    const badgeEl = root.querySelector('[data-mp-sale-badge]');
    const skuEl = root.querySelector('[data-mp-sku]');
    const soundWrap = root.querySelector('[data-mp-sound]');
    const soundBtn = root.querySelector('[data-mp-sound-btn]');
    const soundAudio = root.querySelector('[data-mp-sound-audio]');
    const soundTitleEl = root.querySelector('[data-mp-sound-title]');
    const soundTimeEl = root.querySelector('[data-mp-sound-time]');
    const soundWave = root.querySelector('[data-mp-sound-wave]');
    const tuneInput = root.querySelector('[data-mp-custom-tune]');
    const tunePriceEl = root.querySelector('[data-mp-custom-tune-price]');
    const tuneMods = root.querySelector('[data-mp-custom-tune-mods]');
    if (tuneInput && tuneMods) {
      tuneInput.addEventListener('change', () => {
        if (tuneInput.checked) {
          window.setTimeout(() => tuneMods.focus(), 0);
        }
      });
    }

    // Quantity stepper
    const qtyInput = root.querySelector('[data-mp-qty]');
    root.querySelectorAll('[data-mp-qty-btn]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const step = parseInt(btn.getAttribute('data-mp-qty-btn'), 10) || 1;
        const current = parseInt(qtyInput.value, 10) || 1;
        qtyInput.value = Math.max(1, current + step);
      });
    });

    // Thumb swaps main image or deferred video
    root.querySelectorAll('[data-mp-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const type = thumb.getAttribute('data-mp-type') || 'image';
        root.querySelectorAll('.dd-mp__thumb').forEach((t) => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
        if (type === 'external_video' || type === 'video') {
          showMainVideo(root, {
            type: type,
            poster: thumb.getAttribute('data-mp-poster'),
            embed: thumb.getAttribute('data-mp-embed'),
            videoSrc: thumb.getAttribute('data-mp-video-src'),
          });
        } else {
          showMainImage(root, thumb.getAttribute('data-mp-src') || thumb.getAttribute('data-mp-poster'));
        }
      });
    });

    const videoPlayBtn = root.querySelector('[data-mp-video-play]');
    if (videoPlayBtn) {
      videoPlayBtn.addEventListener('click', () => {
        playMainVideo(root.querySelector('[data-mp-main-video]'));
      });
    }

    // Variant switching
    optionInputs.forEach((input) => input.addEventListener('change', onOptionChange));

    const initialSelected = [];
    root.querySelectorAll('.dd-mp__form [data-mp-option-name]').forEach((fs) => {
      initialSelected.push(getOptionValue(fs));
    });
    const initialVariant = variants.find((v) => arraysEqual(v.options, initialSelected)) || variants[0];
    updateStickyVariantLabel(initialVariant);
    syncCustomTuneAddon(initialVariant);
    syncSound(initialVariant);

    if (soundWave && !soundWave.childElementCount) {
      const barHeights = [28, 42, 55, 38, 70, 48, 62, 85, 50, 36, 74, 58, 44, 90, 66, 40, 78, 52, 34, 68, 46, 82, 60, 32, 72, 54, 88, 42, 64, 48, 76, 38, 58, 84, 46, 70, 36, 62, 50, 30];
      barHeights.forEach((height) => {
        const bar = document.createElement('span');
        bar.className = 'dd-mp__sound-bar';
        bar.style.height = Math.max(4, Math.round(height * 0.4)) + 'px';
        soundWave.appendChild(bar);
      });
    }

    if (soundWrap && soundAudio) {
      soundWrap.addEventListener('click', () => {
        if (!soundAudio.getAttribute('src')) return;
        if (!soundAudio.paused && !soundAudio.ended) soundAudio.currentTime = 0;
        const pending = soundAudio.play();
        if (pending && typeof pending.catch === 'function') pending.catch(function () {});
      });
      soundAudio.addEventListener('play', () => soundBtn.classList.add('is-playing'));
      soundAudio.addEventListener('ended', () => {
        soundBtn.classList.remove('is-playing');
        setSoundProgress(1);
      });
      soundAudio.addEventListener('pause', () => {
        if (!soundAudio.ended) soundBtn.classList.remove('is-playing');
      });
      soundAudio.addEventListener('loadedmetadata', updateSoundDuration);
      soundAudio.addEventListener('durationchange', updateSoundDuration);
      soundAudio.addEventListener('timeupdate', () => {
        if (!soundAudio.duration) return;
        setSoundProgress(soundAudio.currentTime / soundAudio.duration);
      });
      if (soundAudio.readyState >= 1) updateSoundDuration();
    }

    if (soundWave && soundAudio) {
      soundWave.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        if (!soundAudio.duration) return;
        event.preventDefault();
        const step = soundAudio.duration * 0.05;
        const next = event.key === 'ArrowRight'
          ? Math.min(soundAudio.duration, soundAudio.currentTime + step)
          : Math.max(0, soundAudio.currentTime - step);
        soundAudio.currentTime = next;
        setSoundProgress(next / soundAudio.duration);
      });
    }

    function onOptionChange() {
      const selected = [];
      root.querySelectorAll('.dd-mp__form [data-mp-option-name]').forEach((fs) => {
        selected.push(getOptionValue(fs));
      });
      const variant = variants.find((v) => arraysEqual(v.options, selected));
      if (!variant) return disable('Unavailable');
      applyVariant(variant);
    }

    function buyLabelForVariant(variant) {
      if (!variant.available) return 'Sold Out';
      if (
        variant.inventory_management === 'shopify' &&
        variant.inventory_quantity < 1 &&
        variant.inventory_policy === 'continue'
      ) {
        return 'Pre Order';
      }
      return 'Add to Cart';
    }

    function applyVariant(variant) {
      if (idInput) idInput.value = variant.id;
      if (priceEl) priceEl.textContent = formatMoney(variant.price);
      if (skuEl) {
        skuEl.textContent = variant.sku || '';
        const skuWrap = skuEl.closest('.dd-mp__sku');
        if (skuWrap) skuWrap.hidden = !variant.sku;
      }
      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.textContent = formatMoney(variant.compare_at_price);
          compareEl.hidden = false;
          if (saveEl) {
            saveEl.textContent = 'On Sale';
            saveEl.hidden = false;
          }
          if (badgeEl) {
            badgeEl.textContent = 'On Sale';
            badgeEl.hidden = false;
          }
        } else {
          compareEl.hidden = true;
          if (saveEl) saveEl.hidden = true;
          if (badgeEl) badgeEl.hidden = true;
        }
      }
      if (variant.featured_image) {
        const imgSrc = variant.featured_image.src.replace(/(\.[a-z]+)(\?.*)?$/i, '_1200x1200$1');
        showMainImage(root, imgSrc);
      }
      if (addBtn) {
        addBtn.disabled = !variant.available;
        if (addLabel) addLabel.textContent = buyLabelForVariant(variant);
      }
      // Sync sticky footer + variant modal
      const stickyPrice = document.querySelector('[data-mp-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
      const modalPrice = document.querySelector('[data-mp-modal-price]');
      if (modalPrice) modalPrice.textContent = formatMoney(variant.price);
      const stickyAddBtn = document.querySelector('[data-mp-sticky-add]');
      const stickyAddLabel = document.querySelector('[data-mp-sticky-add-label]');
      if (stickyAddBtn) stickyAddBtn.disabled = !variant.available;
      if (stickyAddLabel) stickyAddLabel.textContent = buyLabelForVariant(variant);
      updateStickyVariantLabel(variant);
      const modalAddBtn = document.querySelector('[data-mp-modal-add]');
      const modalAddLabel = document.querySelector('[data-mp-modal-add-label]');
      if (modalAddBtn) modalAddBtn.disabled = !variant.available;
      if (modalAddLabel) modalAddLabel.textContent = buyLabelForVariant(variant);
      syncCustomTuneAddon(variant);
      syncSound(variant);
      root.dispatchEvent(new CustomEvent('dd:variant-change', { detail: { variant: variant } }));
      // Sync URL
      if (window.history && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    function syncSound(variant) {
      if (!soundWrap || !soundAudio) return;
      updateSoundTitle(variant);
      const nextUrl = (variant && variant.mp3_url) || '';
      if (!nextUrl) {
        soundAudio.pause();
        soundAudio.removeAttribute('src');
        soundWrap.hidden = true;
        if (soundBtn) soundBtn.classList.remove('is-playing');
        setSoundProgress(0);
        if (soundTimeEl) soundTimeEl.textContent = '';
        return;
      }
      soundWrap.hidden = false;
      if ((soundAudio.getAttribute('src') || '') === nextUrl) return;
      soundAudio.pause();
      soundAudio.setAttribute('src', nextUrl);
      soundAudio.load();
      if (soundBtn) soundBtn.classList.remove('is-playing');
      setSoundProgress(0);
      if (soundTimeEl) soundTimeEl.textContent = '';
    }

    function updateSoundTitle(variant) {
      if (!soundTitleEl) return;
      const options = (variant && variant.options) || [];
      const title = options.filter((opt) => opt && opt !== 'Default Title').join(' / ');
      soundTitleEl.textContent = title;
      soundTitleEl.hidden = !title;
    }

    function updateSoundDuration() {
      if (!soundTimeEl || !soundAudio) return;
      const seconds = soundAudio.duration;
      if (!Number.isFinite(seconds) || seconds < 0) {
        soundTimeEl.textContent = '';
        return;
      }
      const total = Math.round(seconds);
      if (total < 60) {
        soundTimeEl.textContent = total + 's';
        return;
      }
      const mins = Math.floor(total / 60);
      const secs = total % 60;
      soundTimeEl.textContent = mins + ':' + String(secs).padStart(2, '0');
    }

    function setSoundProgress(ratio) {
      if (!soundWave) return;
      const bars = soundWave.querySelectorAll('.dd-mp__sound-bar');
      const clamped = Math.max(0, Math.min(1, ratio || 0));
      bars.forEach((bar, index) => {
        bar.classList.toggle('is-played', bars.length > 0 && index / bars.length < clamped);
      });
      soundWave.setAttribute('aria-valuenow', String(Math.round(clamped * 100)));
    }

    function disable(label) {
      if (addBtn) addBtn.disabled = true;
      if (addLabel) addLabel.textContent = label || 'Unavailable';
    }

    function pickAddonVariant(variant) {
      if (!addonVariants.length) return null;
      if (addonVariants.length === 1) return addonVariants[0];
      const selected = (variant && variant.options) || [];
      let best = null;
      let bestScore = -1;
      addonVariants.forEach((addon) => {
        const score = scoreAddonMatch(addon.options || [], selected);
        if (score > bestScore) {
          bestScore = score;
          best = addon;
        }
      });
      return (best && bestScore > 0) ? best : addonVariants[0];
    }

    function scoreAddonMatch(addonOptions, selected) {
      let score = 0;
      selected.forEach((sel) => {
        addonOptions.forEach((opt) => {
          if (!opt || opt === 'Default Title' || !sel) return;
          if (opt === sel) {
            score += 8;
            return;
          }
          const a = tokenizeOption(opt);
          const b = tokenizeOption(sel);
          if (!a.length || !b.length) return;
          const inB = {};
          b.forEach((t) => { inB[t] = true; });
          let shared = 0;
          a.forEach((t) => { if (inB[t]) shared += 1; });
          if (shared) score += shared;
        });
      });
      return score;
    }

    function tokenizeOption(opt) {
      return String(opt)
        .toUpperCase()
        .replace(/[^A-Z0-9.]+/g, ' ')
        .trim()
        .split(/\s+/)
        .filter(Boolean);
    }

    function syncCustomTuneAddon(variant) {
      if (!tuneInput) return;
      const addon = pickAddonVariant(variant);
      if (addon && addon.id) {
        tuneInput.setAttribute('data-addon-id', String(addon.id));
        if (tunePriceEl && typeof addon.price === 'number') {
          tunePriceEl.textContent = '+ ' + formatMoney(addon.price);
        }
      } else {
        tuneInput.removeAttribute('data-addon-id');
      }
    }
  }

  function getOptionValue(group) {
    if (!group) return null;
    const checked = group.querySelector('[data-mp-option-input]:checked, [data-mp-modal-option-input]:checked');
    if (checked) return checked.value;
    const select = group.querySelector('select[data-mp-option-input], select[data-mp-modal-option-input]');
    return select ? select.value : null;
  }

  function setOptionValue(group, value) {
    if (!group) return null;
    const select = group.querySelector('select[data-mp-option-input], select[data-mp-modal-option-input]');
    if (select) {
      select.value = value;
      return select;
    }
    const radios = group.querySelectorAll('[data-mp-option-input], [data-mp-modal-option-input]');
    let matched = null;
    radios.forEach((radio) => {
      radio.checked = radio.value === value;
      if (radio.checked) matched = radio;
    });
    return matched;
  }

  function findOptionGroup(scope, name) {
    if (!scope) return null;
    return Array.prototype.find.call(
      scope.querySelectorAll('[data-mp-option-name]'),
      (el) => el.getAttribute('data-mp-option-name') === name
    );
  }

  function formatStickyVariantLabel(variant) {
    const options = (variant && variant.options) || [];
    return options.filter((opt) => opt && opt !== 'Default Title').join(' / ');
  }

  function updateStickyVariantLabel(variant) {
    const el = document.querySelector('[data-mp-sticky-fit]');
    if (!el) return;
    const label = formatStickyVariantLabel(variant);
    el.textContent = label;
    el.hidden = !label;
    el.classList.remove('is-fit', 'is-no-fit');
  }

  function arraysEqual(a, b) {
    if (!a || !b || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  }

  function formatMoney(cents) {
    if (typeof cents !== 'number') return '';
    const value = (cents / 100).toFixed(2);
    const parts = value.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + parts.join('.');
  }

  // Prefer DOMContentLoaded so all head-deferred vehicle libs have run.
  // If this file itself is mid-deferred-queue, one macrotask waits for the rest.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    setTimeout(init, 0);
  }
})();
